'use client';

/**
 * PageTitle — the masthead title used on every top-level page.
 *
 * Two-part recipe: a heavy display-caps HEAD ("PROJECTS", "YARNS",
 * "TOOLS") followed by an italic Fraunces lowercase TAIL ("finished",
 * "in my stash", "and accessories"). Reads as a magazine headline.
 *
 * Default scale matches the production masthead at 44/30. Pass
 * `scale="md"` for the smaller in-page sub-mastheads.
 *
 * Renders as an `<h1>` by default — the page-level title. Pass
 * `level={2}` if it's nested inside another titled region (rare).
 *
 * Surrounding chrome (sticky header, back / add buttons, halftone
 * backgrounds, sheep strips) stays in the page — this primitive
 * is JUST the title. It replaces the boilerplate <h1><span></span><span></span></h1>
 * pattern that was duplicated across all 8 top-level pages
 * (/projects, /active, /yarn, /inventory, /inspiration, /techniques,
 * /profile, /settings).
 */

import type { CSSProperties } from 'react';

export type PageTitleScale = 'lg' | 'md';

export type PageTitleProps = {
  /** First word(s), rendered in display-caps. */
  head: string;
  /** Optional italic-serif suffix. Auto-lowercased. */
  tail?: string;
  /** Heading level. Default 1. */
  level?: 1 | 2;
  /** Size preset. lg = 44/30 (default), md = 30/22. */
  scale?: PageTitleScale;
  /** Outer style overrides (color, margin, etc.). */
  style?: CSSProperties;
  className?: string;
};

const SCALES: Record<PageTitleScale, { headSize: number; tailSize: number }> = {
  lg: { headSize: 44, tailSize: 30 },
  md: { headSize: 30, tailSize: 22 },
};

export function PageTitle({
  head,
  tail,
  level = 1,
  scale = 'lg',
  style,
  className,
}: PageTitleProps) {
  const s = SCALES[scale];
  const baseStyle: CSSProperties = {
    margin: 0,
    color: 'var(--ink)',
    lineHeight: 1.05,
    ...style,
  };

  const content = (
    <>
      <span
        style={{
          fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
          fontWeight: 800,
          fontSize: s.headSize,
          letterSpacing: '-0.02em',
          textTransform: 'uppercase',
          lineHeight: 0.95,
        }}
      >
        {head}
      </span>
      {tail && (
        <>
          {' '}
          <span
            style={{
              fontFamily: "'Fraunces', 'Times New Roman', serif",
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: s.tailSize,
              letterSpacing: '-0.01em',
              color: 'var(--ink)',
            }}
          >
            {tail.toLowerCase()}
          </span>
        </>
      )}
    </>
  );

  if (level === 2) {
    return (
      <h2 className={className} style={baseStyle}>
        {content}
      </h2>
    );
  }
  return (
    <h1 className={className} style={baseStyle}>
      {content}
    </h1>
  );
}
